import type {
  IntendedMove,
  ResolvedOperation,
} from "@/components/FileConflictResolver";
import {
  asDraggableData,
  type DraggableData,
  type DroppableData,
} from "@/dnd/ids";
import { buildCopyPayloadFromRecord } from "@/dnd/payloads";
import type { TransferDestination } from "@/dnd/types";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import type {
  Canvas,
  CopyFileRecordPayload,
  FileRecord,
  InboxItem,
  MoveFileRecordPayload,
  UpdateCanvasRequest,
} from "@cohesive/api-client";
import type { Active } from "@dnd-kit/core";
import toast from "react-hot-toast";

export type DragItemKind = "canvas" | "folder" | "file" | "chat";

function parseSelectionId(
  id: string,
): { kind: DragItemKind; itemId: string } | null {
  const sep = id.indexOf(":");
  if (sep === -1) return null;
  const kind = id.slice(0, sep) as DragItemKind;
  const itemId = id.slice(sep + 1);
  if (!["canvas", "folder", "file", "chat"].includes(kind)) return null;
  return { kind, itemId };
}

function getBaseName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || cleaned;
}

/**
 * Build unified draggable data from a workspace detail multi-select snapshot.
 */
export function buildWorkspaceDetailDraggable(
  selectionIds: string[],
  sourceWorkspaceId: string,
  canvasMap: Map<string, Canvas>,
  fileRecordMap: Map<string, FileRecord>,
  inboxItemMap?: Map<string, InboxItem>,
): DraggableData | null {
  const parsed = selectionIds
    .map(parseSelectionId)
    .filter((p): p is NonNullable<typeof p> => p !== null);
  if (parsed.length === 0) return null;

  const canvasIds = parsed
    .filter((p) => p.kind === "canvas")
    .map((p) => p.itemId);
  const fileRecordIds = parsed
    .filter((p) => p.kind === "file" || p.kind === "folder")
    .map((p) => p.itemId);
  const chatIds = parsed.filter((p) => p.kind === "chat").map((p) => p.itemId);

  // The chat-session payload carries a single chat (like the rail rows), so
  // only a pure single-chat selection becomes a unified draggable. Multi-chat
  // and chat-mixed selections fall back to the snapshot path, which fails
  // closed (see isMixedWorkspaceDetailSelection / classifyMixed…).
  if (chatIds.length > 0) {
    if (
      chatIds.length > 1 ||
      canvasIds.length > 0 ||
      fileRecordIds.length > 0
    ) {
      return null;
    }
    const item = inboxItemMap?.get(chatIds[0]);
    if (!item) return null;
    return {
      kind: "chat-session",
      payload: {
        flowSessionId: item.flow_session_id,
        sourceCanvasId: "",
        name: item.flow_session?.name || "Untitled chat",
        inboxItemId: item.id,
        sourceWorkspaceId: item.flow_session?.workspace_id,
        sourceFolderId: item.flow_session?.folder_id ?? null,
      },
    };
  }

  if (canvasIds.length > 0 && fileRecordIds.length === 0) {
    const primary =
      canvasMap.get(canvasIds[0])?.name ?? `${canvasIds.length} canvases`;
    return {
      kind: "canvas-card",
      canvasIds,
      // The canvas's own workspace beats the hosting surface's: home lists
      // canvases across workspaces, so a single context id would mislabel
      // provenance there. On the detail page the two always coincide.
      sourceWorkspaceId:
        canvasMap.get(canvasIds[0])?.workspace_id ?? sourceWorkspaceId,
      // Folder provenance is single-canvas only (see DraggableCanvasCardData) —
      // it's what lets a root drop un-folder and a same-folder drop no-op.
      sourceFolderId:
        canvasIds.length === 1
          ? (canvasMap.get(canvasIds[0])?.folder_id ?? null)
          : undefined,
      primaryName: primary,
    };
  }

  if (fileRecordIds.length > 0 && canvasIds.length === 0) {
    const payloads = fileRecordIds
      .map((id) => {
        const record = fileRecordMap.get(id);
        if (!record) return null;
        // Scope from the record itself, context id as fallback only: home
        // rows span workspaces (and shared rows can be container-class), so
        // provenance must travel with each record. Detail-page records all
        // carry the context's workspace, so nothing changes there.
        return buildCopyPayloadFromRecord(record, {
          workspaceId: record.workspace_id ?? sourceWorkspaceId,
          canvasId: record.canvas_id ?? undefined,
          flowSessionId: record.flow_session_id ?? undefined,
        });
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);
    if (payloads.length === 0) return null;
    return {
      kind: "files",
      payloads,
      primaryName:
        payloads.length === 1 ? payloads[0].name : `${payloads.length} items`,
    };
  }

  return null;
}

export function dragSelectionHasCanvasFromSnapshot(
  selectionIds: string[],
): boolean {
  return selectionIds.some(
    (id) => typeof id === "string" && id.startsWith("canvas:"),
  );
}

export function isMixedWorkspaceDetailSelection(
  selectionIds: string[],
): boolean {
  if (selectionIds.length === 0) return false;
  const hasCanvas = dragSelectionHasCanvasFromSnapshot(selectionIds);
  const hasFiles = selectionIds.some(
    (id) => id.startsWith("file:") || id.startsWith("folder:"),
  );
  const chatCount = selectionIds.filter((id) => id.startsWith("chat:")).length;
  // Chats only move one at a time (the chat-session payload carries a single
  // chat), so any multi-chat or chat+other selection is "mixed" — routed to
  // the snapshot path so the pill/dispatcher can reject it explicitly.
  if (chatCount > 1) return true;
  if (chatCount === 1 && (hasCanvas || hasFiles)) return true;
  return hasCanvas && hasFiles;
}

export type MixedWorkspaceDetailDropClassification =
  | { kind: "move"; targetLabel: string }
  | { kind: "invalid"; reason: string };

/**
 * Mixed canvas+file workspace-detail drags only carry `selectionSnapshot`.
 * Homogeneous selections use `classifyTransfer`; this covers the legacy
 * multi-kind snapshot on each droppable surface.
 */
export function classifyMixedWorkspaceDetailDrop(
  droppable: DroppableData,
  selectionIds?: string[],
): MixedWorkspaceDetailDropClassification {
  const { surface, destination } = droppable;

  // The mixed workspace-root handler only moves canvases and files — a chat
  // in the snapshot would be silently left behind, so fail closed instead.
  if (selectionIds?.some((id) => id.startsWith("chat:"))) {
    const onlyChats = selectionIds.every(
      (id) => id.startsWith("chat:") || parseSelectionId(id) === null,
    );
    return {
      kind: "invalid",
      reason: onlyChats
        ? "Chats move one at a time"
        : "Can't move chats with other items",
    };
  }

  if (
    (surface === "workspace-folder" || surface === "rail-workspace-folder") &&
    destination.kind === "workspace"
  ) {
    if (destination.parentPath !== "") {
      return {
        kind: "invalid",
        reason: "Can't move canvases into folders",
      };
    }
    return {
      kind: "move",
      targetLabel: "workspace",
    };
  }

  if (surface === "chat-composer") {
    return {
      kind: "invalid",
      reason: "Can't attach mixed selection to chat",
    };
  }

  if (
    surface === "canvas-board" ||
    surface === "canvas-section" ||
    surface === "canvas-scope"
  ) {
    return {
      kind: "invalid",
      reason: "Can't drop mixed selection on canvas",
    };
  }

  if (surface === "rail-favorites") {
    return {
      kind: "invalid",
      reason: "Can't favorite mixed selection",
    };
  }

  return { kind: "invalid", reason: "Can't drop mixed selection here" };
}

export function dragActiveHasCanvas(active: Active | null): boolean {
  if (!active) return false;
  const draggable = asDraggableData(active.data.current);
  if (draggable?.kind === "canvas-card") return true;
  const snapshot = active.data.current?.selectionSnapshot;
  if (Array.isArray(snapshot)) {
    return dragSelectionHasCanvasFromSnapshot(snapshot);
  }
  return active.data.current?.kind === "canvas";
}

interface WorkspaceDetailDropParams {
  selectionIds: string[];
  destination: Extract<TransferDestination, { kind: "workspace" }>;
  sourceWorkspaceId: string;
  canvasMap: Map<string, Canvas>;
  fileRecordMap: Map<string, FileRecord>;
  updateCanvas: (vars: {
    id: string;
    data: UpdateCanvasRequest;
  }) => Promise<unknown>;
  moveFileRecord: (vars: {
    fileRecordId: string;
    payload: MoveFileRecordPayload;
  }) => Promise<unknown>;
  copyFileRecord: (vars: {
    fileRecordId: string;
    payload: CopyFileRecordPayload;
  }) => Promise<unknown>;
  deleteFileRecord: (fileRecordId: string) => Promise<unknown>;
  resolveMoves: (params: {
    moves: IntendedMove[];
    sourceRecords: FileRecord[];
    destinationWorkspaceId: string;
  }) => Promise<ResolvedOperation[] | null>;
  targetLabel: string;
}

/**
 * Mixed canvas + file drops from workspace detail pages. Homogeneous
 * selections route through `classifyTransfer`; this handles the legacy
 * multi-kind snapshot the grid still produces.
 */
export async function runWorkspaceDetailMixedDrop({
  selectionIds,
  destination,
  sourceWorkspaceId,
  canvasMap,
  fileRecordMap,
  updateCanvas,
  moveFileRecord,
  copyFileRecord,
  deleteFileRecord,
  resolveMoves,
  targetLabel,
}: WorkspaceDetailDropParams): Promise<void> {
  const parsed = selectionIds
    .map(parseSelectionId)
    .filter((p): p is NonNullable<typeof p> => p !== null);
  if (parsed.length === 0) return;

  const canvasIds = parsed
    .filter((p) => p.kind === "canvas")
    .map((p) => p.itemId);
  const fileRecordIds = parsed
    .filter((p) => p.kind === "file" || p.kind === "folder")
    .map((p) => p.itemId);

  if (destination.parentPath !== "" && canvasIds.length > 0) {
    toast("Canvases can't be moved", { icon: "⛔︎" });
    return;
  }

  for (const frId of fileRecordIds) {
    const record = fileRecordMap.get(frId);
    if (
      record &&
      record.path.endsWith("/") &&
      destination.parentPath !== "" &&
      record.workspace_id === destination.workspaceId
    ) {
      const targetPath = destination.parentPath;
      if (targetPath.startsWith(record.path)) {
        toast.error("Cannot move a folder into itself");
        return;
      }
    }
  }

  const promises: Promise<void>[] = [];

  for (const cId of canvasIds) {
    const canvas = canvasMap.get(cId);
    if (!canvas) continue;
    if (canvas.workspace_id === destination.workspaceId) continue;
    promises.push(
      updateCanvas({
        id: cId,
        data: {
          name: canvas.name,
          description: canvas.description,
          metadata: canvas.metadata,
          workspace_id: destination.workspaceId,
        },
      }).then(() => undefined),
    );
  }

  const destPath = destination.parentPath;
  const intendedMoves: IntendedMove[] = [];
  for (const frId of fileRecordIds) {
    const record = fileRecordMap.get(frId);
    if (!record) continue;

    const baseName = getBaseName(record.path);
    const isFolder = record.path.endsWith("/");
    const fullDestPath = destPath
      ? `${destPath}${isFolder ? `${baseName}/` : baseName}`
      : isFolder
        ? `${baseName}/`
        : baseName;

    if (
      destination.workspaceId === sourceWorkspaceId &&
      fullDestPath === record.path
    ) {
      continue;
    }

    intendedMoves.push({
      fileRecordId: frId,
      sourceName: baseName,
      destinationParentPath: destPath,
      isFolder,
    });
  }

  if (intendedMoves.length > 0) {
    const sourceRecords = Array.from(fileRecordMap.values());
    const ops = await resolveMoves({
      moves: intendedMoves,
      sourceRecords,
      destinationWorkspaceId: destination.workspaceId,
    });
    if (ops === null) return;

    const allRecords = Array.from(fileRecordMap.values());
    for (const op of ops) {
      promises.push(
        (async () => {
          switch (op.kind) {
            case "move": {
              const sourceRecord = fileRecordMap.get(op.fileRecordId);
              if (!sourceRecord) {
                await moveFileRecord({
                  fileRecordId: op.fileRecordId,
                  payload: op.payload,
                });
                return;
              }
              await moveRecordCascading({
                record: sourceRecord,
                payload: op.payload,
                allRecords,
                moveOne: moveFileRecord,
              });
              return;
            }
            case "replace_contents":
              await copyFileRecord({
                fileRecordId: op.sourceFileRecordId,
                payload: op.payload,
              });
              return;
            case "delete":
              await deleteFileRecord(op.fileRecordId);
              return;
          }
        })(),
      );
    }
  }

  if (promises.length === 0) return;

  const totalCount = promises.length;
  const results = await Promise.allSettled(promises);
  const failures = results.filter((r) => r.status === "rejected").length;
  const successes = totalCount - failures;

  if (failures === 0) {
    toast.success(
      successes === 1
        ? `Moved 1 item to ${targetLabel}`
        : `Moved ${successes} items to ${targetLabel}`,
    );
  } else if (successes === 0) {
    toast.error(
      totalCount === 1
        ? "Failed to move item"
        : `Failed to move ${totalCount} items`,
    );
  } else {
    toast.error(
      `Moved ${successes} of ${totalCount} items, ${failures} failed`,
    );
  }
}
